import { spanFromRegExpMatch } from '../spans';
import type { ExtractorOutput } from './types';
import type { ParsedHomeType } from '../types';

const EMPTY_HOME_TYPE: ParsedHomeType = {
  house: false,
  condo: false,
  townhouse: false,
  farm: false,
  land: false,
  commercial: false,
};

interface HomeTypeRule {
  field: keyof ParsedHomeType;
  pattern: RegExp;
  ruleId: string;
}

/**
 * Order matters: townhouse/farmhouse synonyms contain "houses" and must be
 * matched (and spanned) before the generic house pattern.
 */
const HOME_TYPE_RULES: ReadonlyArray<HomeTypeRule> = [
  {
    field: 'townhouse',
    pattern: /\b(?:townhouses?|townhomes?|row\s+houses?)\b/gi,
    ruleId: 'homeType.townhouse',
  },
  {
    field: 'farm',
    pattern: /\b(?:farms?|farmhouses?|acreages?)\b/gi,
    ruleId: 'homeType.farm',
  },
  {
    field: 'house',
    pattern: /\b(?:houses?|detached|single\s+family|single-family|bungalows?)\b/gi,
    ruleId: 'homeType.house',
  },
  {
    field: 'condo',
    pattern: /\b(?:condos?|apartments?|condominiums?)\b/gi,
    ruleId: 'homeType.condo',
  },
  {
    field: 'land',
    pattern: /\b(?:vacant\s+land|land|lots?(?!\s+of))\b/gi,
    ruleId: 'homeType.land',
  },
  {
    field: 'commercial',
    pattern: /\b(?:commercial|office|retail|industrial)\b/gi,
    ruleId: 'homeType.commercial',
  },
];

export function extractHomeType(query: string): ExtractorOutput {
  // "open house" is a feature, not a property type — mask before matching.
  const openHouseMasked = query.replace(/\bopen\s+house\b/gi, match => ' '.repeat(match.length));

  const matched: Partial<ParsedHomeType> = {};
  const spans: ReturnType<typeof spanFromRegExpMatch>[] = [];
  // Track already-covered ranges so later generic patterns don't re-hit
  // characters already claimed by townhouse/farm rules.
  const claimed: boolean[] = Array.from({ length: query.length }, () => false);

  const markClaimed = (start: number, end: number) => {
    for (let i = start; i < end; i++) claimed[i] = true;
  };
  const overlapsClaimed = (start: number, end: number) => {
    for (let i = start; i < end; i++) {
      if (claimed[i]) return true;
    }
    return false;
  };

  for (const rule of HOME_TYPE_RULES) {
    rule.pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    // Work against the open-house-masked string for matching, but record spans
    // against the original query (indices align because we only replace with spaces).
    while ((match = rule.pattern.exec(openHouseMasked)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      if (overlapsClaimed(start, end)) continue;
      matched[rule.field] = true;
      markClaimed(start, end);
      spans.push(
        spanFromRegExpMatch(query, match, 'homeType', rule.field, {
          confidence: 0.95,
          ruleId: rule.ruleId,
        })
      );
    }
  }

  if (Object.keys(matched).length === 0) {
    return { partial: {}, spans: [] };
  }

  return {
    partial: {
      homeType: {
        ...EMPTY_HOME_TYPE,
        ...matched,
      },
    },
    spans,
  };
}
